import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowLeft, Mail, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setEmailSent(true);
      toast.success('Password reset link sent! Please check your email.');
    } catch (error) {
      // Backend always returns success message for security
      setEmailSent(true);
      toast.success('If that email exists, a password reset link has been sent.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Background */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ 
          backgroundImage: 'url(https://images.unsplash.com/photo-1679615845580-8691c78fd7d3?q=80&w=2000)',
          zIndex: 0
        }}
      ></div>
      
      {/* Overlay */}
      <div 
        className="absolute inset-0" 
        style={{ 
          background: 'linear-gradient(to bottom, rgba(10, 14, 26, 0.5) 0%, rgba(26, 35, 50, 0.6) 50%, rgba(45, 55, 72, 0.7) 100%)',
          zIndex: 1
        }}
      ></div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-6 py-20 flex items-center justify-center min-h-screen">
        <div className="max-w-md w-full">
          <Card className="glass-effect border-cyan-500/30">
            <CardContent className="p-8">
              {/* Logo */}
              <div className="text-center mb-6">
                <div className="w-32 h-32 rounded-full overflow-hidden mx-auto mb-4">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_eleven-collective/artifacts/56npyh3g_11-11%20Coaching%20App.jpg" 
                    alt="Logo" 
                    className="w-full h-full object-contain"
                  />
                </div>
                <h2 className="text-3xl font-bold mb-2" style={{ color: '#331a67' }}>
                  Forgot Password?
                </h2>
                <p className="text-sm" style={{ color: '#445277' }}>
                  No worries! Enter your email and we'll send you reset instructions.
                </p>
              </div>

              {!emailSent ? (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: '#331a67' }}>
                      Email Address
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: '#445277' }} />
                      <Input
                        id="forgot-email"
                        name="email"
                        type="email"
                        autoComplete="username"
                        inputMode="email"
                        placeholder="your.email@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        className="pl-10 bg-white border-cyan-500/30 placeholder:text-gray-400"
                        style={{ color: '#000000' }}
                      />
                    </div>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full btn-primary" 
                    disabled={loading}
                  >
                    {loading ? 'Sending...' : 'Send Reset Link'}
                  </Button>

                  <Link to="/">
                    <Button 
                      type="button" 
                      variant="ghost" 
                      className="w-full"
                      style={{ color: '#331a67' }}
                    >
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Back to Login
                    </Button>
                  </Link>
                </form>
              ) : (
                <div className="text-center space-y-4">
                  <div className="flex justify-center">
                    <CheckCircle className="w-16 h-16" style={{ color: '#00bcd4' }} />
                  </div>
                  <h3 className="text-xl font-semibold" style={{ color: '#331a67' }}>
                    Check Your Email
                  </h3>
                  <p className="text-sm" style={{ color: '#445277' }}>
                    We've sent password reset instructions to <strong>{email}</strong>
                  </p>
                  <p className="text-xs" style={{ color: '#445277' }}>
                    The link will expire in 1 hour for security reasons.
                  </p>
                  <div className="pt-4 space-y-2">
                    <p className="text-xs" style={{ color: '#445277' }}>
                      Didn't receive the email? Check your spam folder or
                    </p>
                    <Button 
                      onClick={() => {
                        setEmailSent(false);
                        setEmail('');
                      }}
                      variant="outline"
                      size="sm"
                      className="w-full"
                    >
                      Try Another Email
                    </Button>
                  </div>
                  <Link to="/">
                    <Button 
                      type="button" 
                      variant="ghost" 
                      className="w-full mt-4"
                      style={{ color: '#331a67' }}
                    >
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Back to Login
                    </Button>
                  </Link>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
